import moment from "moment";
import { TimeFrequency } from "@app/projects/enumerators";
import { ProjectModule } from "@app/projects/literals";
import { CountyProjectContainerState } from "@app/projects/CountyProjectContext";
import { TrendChartItemType } from "@app/velvet/enumerators";

interface BarData {
  byGroupIndex: {
    [Index: number]: {
      [Color: string]: number;
    };
  };
  colorOrderByGroupIndex: {
    [Index: number]: Array<VelvetColors | string>;
  };
  disabled: boolean;
  modeByGroupIndex: {
    [Index: number]: "value" | "percentage";
  };
  outlineId?: string;
  radioId?: string;
  supplementaryTooltip?: string;
  timestamp: number;
  tooltip: string;
  unpublished: boolean;
}

interface Colors {
  BlueLine: string;
  GreenLine: string;
  YellowBar: string;
  RedBar: string;
}

interface LineData {
  disabled: boolean;
  outlineId?: string;
  pointsByColor: { [color: string]: { value: number } };
  radioId?: string;
  timestamp: number;
}

enum VelvetColors {
  Blue = "#002060",
  BlueLight = "#056EDF",
  Green = "#00CD87",
  GreenDark = "#016F4A",
  Red = "#F23957",
  Yellow = "#FFD654",
}

/** Necessary values for rendering a velvet */
interface VelvetConfiguration {
  compareToRunConfigurationId?: number;
  compareToRunConfigurationTimestamp?: moment.Moment;
  dualMembershipConfigurationId?: number;
  isComparingResults?: boolean;
  isDualMembership?: boolean;
  items: VelvetItem[];
  labels: VelvetLabel[];
  module: ProjectModule;
  options?: VelvetOptions;
  projectId: number;
  projectRunConfigurationId: number;
  projectRunConfigurationTimestamp: moment.Moment;
  setRunConfiguration: (runConfigurationId: number, runConfigurationTimestamp?: moment.Moment, isCompareTo?: boolean, runFrequency?: TimeFrequency, delayUpdate?: boolean) => Promise<Partial<CountyProjectContainerState>>;
  supplementaryItems: VelvetItem[];
  updateRunConfigurationState: (newState: Partial<CountyProjectContainerState>) => void;
  use0To100YAxisBounds: boolean,
}

/** The information used to render a label. */
interface VelvetLabel {
  /** Whether to break to a new column after the current item. */
  breakAfter?: boolean;

  /** The color to render the symbol. Must be a hex value. */
  color: VelvetColors | string;

  /** The string displayed alongside the label. */
  name: string;

  /** The orientation of the label on the chart. */
  orientation: "left" | "right";

  /** The type of chart item the label matches to. */
  type: "line" | "bar";

  /** Tooltip on hover, only works for bars. */
  tooltip: string;
}

interface VelvetLinePoint {
  color: VelvetColors | string;
  value: number;
}

interface VelvetBarGroup {
  items: Array<{
    color: VelvetColors | string;
    value: number;
  }>;
  colorOrder: Array<VelvetColors | string>;
  mode?: "value" | "percentage";
}

/** Trend Chart Item model */
interface VelvetItem {
  barGroups: VelvetBarGroup[];
  companionRunConfigurationId?: number;
  disabled: boolean;
  itemType: TrendChartItemType;
  linePoints: VelvetLinePoint[];
  projectRunConfigurationId: number;
  runFrequency: TimeFrequency;
  timestamp: moment.Moment;
  tooltip: string;
  publishComment: string;
}

/** Defines stats for trend chart rendering */
interface VelvetRenderItem extends VelvetItem {
  /** Class that corresponds to whether the item is currently selected and/or in focus (hover) */
  outlineClass?: string;

  /** Id of the outline rectangle */
  outlineId?: string;

  /** Defines the class assigned to this item's radio */
  radioClass: string;

  /** Defines the Font Awesome background text to be rendered */
  radioBgText: string;

  /** Id of the radio */
  radioId?: string;

  /** Callback function for onClick event */
  radioFunction: (e: d3.BaseType, data: unknown, index: number, groups: d3.BaseType[] | ArrayLike<d3.BaseType>) => void | null;

  /** Defines the Font Awesome text to be rendered */
  radioText: string;

  /** Timestamp's month */
  timestampMonth: number;

  /** The item type for this item in the trend chart */
  itemType: TrendChartItemType;

  /** Label for this item */
  xAxisLabel: string;
}

interface VelvetOptions {
  fixedColumnWidths?: boolean;
  showAttestation: boolean;
  showAdvancedComparison: boolean;
}

interface LocalStorageRunConfigurationItem {
  projectRunConfigurationId: number;
  timeInserted: string;
}

export {
  BarData,
  Colors,
  LineData,
  LocalStorageRunConfigurationItem,
  VelvetBarGroup,
  VelvetColors,
  VelvetConfiguration,
  VelvetItem,
  VelvetLabel,
  VelvetLinePoint,
  VelvetOptions,
  VelvetRenderItem
};
